/**
 * RehearsalLayout — centralized copy strings (AOF 25, i18n posture).
 */

export const rehearsalLayoutCopy = {
  /**
   * Why the save control is there, in words rather than in jargon.
   *
   * This read "anonymous session" in a monospaced face — three pieces of
   * console vocabulary sitting above a conversation, telling a visitor
   * their account state in the language of the system rather than saying
   * the one thing they would want to know. It is a real message and it
   * was written for the wrong reader.
   */
  anonymousPill: "Nothing here is saved",
  saveCta: "Save my work",

  /*
   * ## Remember me
   *
   * A button rather than a banner, and the distinction is the feature.
   *
   * A strip along the bottom of the page is the shape everyone has
   * learned to dismiss without reading, so a consent notice in that
   * shape gets consented to by reflex and informs nobody. This sits in
   * the row that already says what does and does not persist, beside
   * the sentence it makes false, where a visitor is already looking for
   * exactly this answer.
   *
   * It is also doing more than consent. Pressing it is the shortest
   * possible explanation of what this product is: a visitor who presses
   * "remember me" has understood that the character will know them next
   * time, in one click, without a paragraph about memory. So the copy is
   * written in her register rather than a policy page's, and the
   * mechanism follows the promise instead of leading it.
   *
   * No em dashes below this line. Visitor-facing copy.
   */

  /** Replaces the pill above once she is keeping something. */
  rememberedPill: "She'll know you next time",

  rememberAction: "Remember me",

  /**
   * What pressing it does, in the visitor's terms, beside the control.
   *
   * Promise first, mechanism second. "A cookie" and "this browser" are
   * both here because they are the two facts a person needs in order to
   * predict what will happen: it will not follow them to their phone,
   * and clearing their browser undoes it.
   */
  rememberNote: "One cookie in this browser, so she knows you next time",

  forgetAction: "Forget me",

  /**
   * What she is actually holding, and for how long.
   *
   * This line said "How this went, not what you said" until #117, and it
   * was accurate for exactly as long as her memory died with the socket:
   * what was stored was a movement in her regard and an authored phrase
   * for why, never the visitor's own words. Persisting conversations
   * made it false, so it changed in the same commit. The claim is a
   * promise to a person about what is kept, which makes it the one
   * string here that cannot lag the server by a release.
   *
   * "Fading as it ages" is the third fact and it is not decoration. An
   * episodic memory decays on a seven-day half-life and stops being
   * retrievable at all somewhere past a fortnight, well inside the
   * thirty days the record itself lasts. A note promising thirty days of
   * what you said, flat, would be over-promising the interesting half.
   *
   * The number is passed in rather than written here. The server owns
   * the duration, this control says it out loud, and a page promising
   * thirty days against a store enforcing seven would be wrong in the
   * one place a reader has no way to check.
   */
  forgetNote: (days: number): string =>
    `What you said and how it went, fading as it ages. This browser, ${days} days`,
} as const;
